import {AttrsSchema, Component, ConfigOptions} from 'panel';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';

import {defineAndMakeJsxFactory} from 'common/util/jsx';

import template from './index.jade';
import css from './index.styl?inline';
import {ScrollControlAttrs, ScrollControlState} from './types';

const MOUSEMOVE_THROTTLE_MS = 200;

export class ScrollControl extends Component<ScrollControlState, ScrollControlAttrs> {
  private _mousemoveEl: HTMLElement;
  private _scrollEl: HTMLElement;
  private handleMouseMove;
  private handleMouseLeave;
  private handleScroll;

  static get attrsSchema(): AttrsSchema<ScrollControlAttrs> {
    return {
      ...super.attrsSchema,
      'scroll-by': {
        type: `number`,
        description: `How much to scroll by in pixels`,
      },
      'scroll-behavior': {
        type: `string`,
        default: `smooth`,
        description: `Set to "auto" to disable smooth scrolling`,
      },
      top: {
        type: `string`,
        default: `50%`,
        description: `Style override`,
      },
      left: {
        type: `string`,
        default: `20px`,
        description: `Style override`,
      },
      right: {
        type: `string`,
        default: `20px`,
        description: `Style override`,
      },
      'interrupt-workaround-delay': {
        type: `number`,
        default: 0,
        description: `Smooth scrolling can be interrupted (e.g. when updating classes when a scroll box becomes the leader via ScrollSyncer). Enables the workaround with given ms delay.`,
      },
    };
  }

  constructor() {
    super();

    this.handleMouseMove = throttle((ev: MouseEvent) => {
      if (!this.state.startControlAvailable && !this.state.endControlAvailable) {
        return;
      }

      const rect = this._mousemoveEl.getBoundingClientRect();
      const offset = rect.left;
      const boundary = rect.width * 0.5 + offset;
      const x = ev.clientX;
      const updates = {startControlVisible: false, endControlVisible: false};
      if (x <= boundary) {
        updates.startControlVisible = true;
      } else {
        updates.endControlVisible = true;
      }
      this.update(updates);
    }, MOUSEMOVE_THROTTLE_MS);

    this.handleMouseLeave = () => {
      setTimeout(() => this.update({startControlVisible: false, endControlVisible: false}), MOUSEMOVE_THROTTLE_MS + 1);
    };

    this.handleScroll = debounce(() => {
      this.updateScrollButtonAvailability();
    }, 10);
  }

  get config(): ConfigOptions<ScrollControlState> {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        startControlAvailable: false,
        endControlAvailable: false,
        startControlVisible: false,
        endControlVisible: false,
      },
      helpers: {
        getStyle: () => {
          return {
            '--top': this.attr(`top`),
            '--left': this.attr(`left`),
            '--right': this.attr(`right`),
          };
        },
        scrollBack: () => this.scrollSteps(true),
        scrollForward: () => this.scrollSteps(),
      },
    };
  }

  updateScrollButtonAvailability() {
    if (!this._scrollEl) {
      return;
    }
    const canScrollForward = this._scrollEl.offsetWidth + this._scrollEl.scrollLeft < this._scrollEl.scrollWidth;
    const canScrollBack = this._scrollEl.scrollLeft > 0;
    this.update({startControlAvailable: canScrollBack, endControlAvailable: canScrollForward});
  }

  /** Element that is scrolled. Scroll position (e.g. all the way left, all the way right or not scrollable at all) determines which buttons are available.  */
  set scrollEl(el: HTMLElement) {
    this._scrollEl?.removeEventListener(`scroll`, this.handleScroll);

    this._scrollEl = el;
    this._scrollEl?.addEventListener(`scroll`, this.handleScroll);
    this.updateScrollButtonAvailability();
  }

  /** Element that is monitored for mousemove and mouseleave events to determine which scroll buttons are visible.
   * This should be a parent so mouseleave events work properly. */
  set mousemoveEl(el: HTMLElement) {
    this._mousemoveEl?.removeEventListener(`mouseleave`, this.handleMouseLeave);
    this._mousemoveEl?.removeEventListener(`mousemove`, this.handleMouseMove);

    this._mousemoveEl = el;
    this._mousemoveEl?.addEventListener(`mouseleave`, this.handleMouseLeave);
    this._mousemoveEl?.addEventListener(`mousemove`, this.handleMouseMove);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this._mousemoveEl?.removeEventListener(`mouseleave`, this.handleMouseLeave);
    this._mousemoveEl?.removeEventListener(`mousemove`, this.handleMouseMove);
    this._scrollEl?.removeEventListener(`scroll`, this.handleScroll);
  }

  private scrollSteps(backward = false) {
    const behavior = this.attr(`scroll-behavior`);
    if (this.attr(`interrupt-workaround-delay`)) {
      const scrollBy = this.attr(`scroll-by`) - 1;
      const left = backward ? -scrollBy : scrollBy;
      this._scrollEl?.scrollBy({left: backward ? -1 : 1, behavior: `smooth`});
      setTimeout(() => this._scrollEl?.scrollBy({left, behavior}), this.attr(`interrupt-workaround-delay`));
    } else {
      const scrollBy = this.attr(`scroll-by`);
      const left = backward ? -scrollBy : scrollBy;
      this._scrollEl?.scrollBy({left, behavior});
    }
  }
}
export const ScrollControlEl = defineAndMakeJsxFactory(`mp-scroll-control`, ScrollControl);
